/**
 * Prompt → checked shader.
 *
 * Stream the model's answer, parse it, and run it in the caller's validator (a
 * hidden copy of the real runtime). Code that fails gets exactly one repair
 * round with the compiler's message; code that fails twice is never returned,
 * so a bad generation cannot replace a working shader.
 *
 * On Genie credits every request is charged, the repair included. Progress
 * carries the running count (`charges`) and a failed run's error says it was
 * charged twice, so the second charge is never silent. An answer cut off by the
 * model's output limit (`TRUNCATED`) is not repaired: the retry would be cut too.
 */

import { parseShaderResponse } from './parse'
import { completeShaderChat, usesGenieCredits } from './providers'
import { abortedError } from './providers/errors'
import { buildRepairPrompt, buildShaderSystemPrompt, buildShaderUserPrompt } from './system-prompt'
import { ShaderGenerationError } from './types'
import type {
  ShaderChatMessage,
  ShaderCodeModelHost,
  ShaderGenerationErrorCode,
  ShaderGenerationPhase,
  ShaderGenerationProgress,
  ShaderGenerationRequest,
  ShaderGenerationResult,
  ShaderProviderConfig,
  ShaderProviderSecrets,
  ShaderValidationResult,
  ShaderValidator,
} from './types'

export interface GenerateShaderOptions {
  request: ShaderGenerationRequest
  config: ShaderProviderConfig
  secrets: ShaderProviderSecrets
  host?: ShaderCodeModelHost | null
  validate: ShaderValidator
  signal: AbortSignal
  onProgress?: (progress: ShaderGenerationProgress) => void
}

type ShaderFailure = Extract<ShaderValidationResult, { ok: false }>

const CHARGED_TWICE = 'Genie credits were charged for both attempts (the first answer and one repair).'

function failureMessage(failure: ShaderFailure): string {
  return failure.line ? `${failure.message} (line ${failure.line})` : failure.message
}

export async function generateShader(options: GenerateShaderOptions): Promise<ShaderGenerationResult> {
  const { request, config, secrets, host, validate, signal, onProgress } = options
  if (!request.prompt.trim()) throw new ShaderGenerationError('Describe the shader you want.', 'UNKNOWN')
  if (request.mode === 'edit' && !request.current) throw new ShaderGenerationError('There is no current shader to edit.', 'UNKNOWN')

  const credits = usesGenieCredits(config, host)
  const system = buildShaderSystemPrompt({ compact: credits })
  let charges = 0
  const ask = (messages: ShaderChatMessage[], phase: ShaderGenerationPhase) => {
    if (credits) charges += 1
    const billed = credits ? { charges } : {}
    onProgress?.({ phase, received: 0, ...billed })
    return completeShaderChat({
      config,
      secrets,
      host,
      system,
      messages,
      signal,
      onText: (text) => onProgress?.({ phase, received: text.length, ...billed }),
    })
  }
  const check = async (result: ShaderGenerationResult): Promise<ShaderValidationResult> => {
    onProgress?.({ phase: 'validating' })
    const outcome = await validate({ code: result.code, runtimeMode: result.runtimeMode, signal })
    if (signal.aborted) throw abortedError()
    return outcome
  }

  const conversation: ShaderChatMessage[] = [{ role: 'user', content: buildShaderUserPrompt(request) }]
  const answer = await ask(conversation, 'generating')
  if (signal.aborted) throw abortedError()
  const first = parseShaderResponse(answer)
  const firstOutcome = await check(first)
  if (firstOutcome.ok) return first

  const repairTurn = buildRepairPrompt(first, firstOutcome)
  const repairedAnswer = await ask([...conversation, { role: 'assistant', content: answer }, { role: 'user', content: repairTurn }], 'repairing')
  if (signal.aborted) throw abortedError()
  // Both answers arrived, so on credits both were paid for: say so in whatever fails next.
  const failed = (message: string, code: ShaderGenerationErrorCode) =>
    credits ? new ShaderGenerationError(`${message}\n\n${CHARGED_TWICE}`, code, { charges }) : new ShaderGenerationError(message, code)
  let repaired: ShaderGenerationResult
  try {
    repaired = parseShaderResponse(repairedAnswer)
  } catch (error) {
    throw error instanceof ShaderGenerationError ? failed(error.message, error.code) : error
  }
  const repairedOutcome = await check(repaired)
  if (!repairedOutcome.ok) throw failed(`The generated shader would not run: ${failureMessage(repairedOutcome)}`, 'VALIDATION')
  // A repair answer may skip the header; the first answer's still describes the shader.
  return { ...repaired, title: repaired.title || first.title, summary: repaired.summary || first.summary }
}
